/**
 * @file zip-writer.ts
 * @description A lightweight, uncompressed (STORED) PKZIP writer.
 * Suitable for Krita (.kra) and other simple ZIP-based formats.
 */

export class ZipWriter {
  private files: { name: string; data: Uint8Array; offset: number; crc: number }[] = [];
  private currentOffset = 0;

  addFile(name: string, data: Uint8Array): void {
    const crc = this.calculateCrc32(data);
    this.files.push({ name, data, offset: this.currentOffset, crc });
    // Local header (30) + name.length + data.length
    this.currentOffset += 30 + name.length + data.length;
  }

  build(): ArrayBuffer {
    const centralDirectorySize = this.files.reduce((acc, f) => acc + 46 + f.name.length, 0);
    const totalSize = this.currentOffset + centralDirectorySize + 22;
    const buffer = new ArrayBuffer(totalSize);
    const view = new DataView(buffer);
    let pos = 0;

    // 1. Write File Data and Local Headers
    for (const file of this.files) {
      file.offset = pos; // Update to actual absolute offset
      view.setUint32(pos, 0x04034b50, true);
      view.setUint16(pos + 4, 10, true); // Version
      view.setUint16(pos + 6, 0, true);  // Flags
      view.setUint16(pos + 8, 0, true);  // Compression (Stored)
      view.setUint32(pos + 10, 0, true); // Time/Date
      view.setUint32(pos + 14, file.crc, true);
      view.setUint32(pos + 18, file.data.length, true);
      view.setUint32(pos + 22, file.data.length, true);
      view.setUint16(pos + 26, file.name.length, true);
      view.setUint16(pos + 28, 0, true); // Extra length

      pos += 30;
      const nameBytes = new TextEncoder().encode(file.name);
      new Uint8Array(buffer, pos, nameBytes.length).set(nameBytes);
      pos += nameBytes.length;
      
      new Uint8Array(buffer, pos, file.data.length).set(file.data);
      pos += file.data.length;
    }

    const centralDirectoryOffset = pos;

    // 2. Write Central Directory
    for (const file of this.files) {
      view.setUint32(pos, 0x02014b50, true);
      view.setUint16(pos + 4, 10, true); // Made by
      view.setUint16(pos + 6, 10, true); // Version
      view.setUint16(pos + 8, 0, true);
      view.setUint16(pos + 10, 0, true);
      view.setUint32(pos + 12, 0, true);
      view.setUint32(pos + 16, file.crc, true);
      view.setUint32(pos + 20, file.data.length, true);
      view.setUint32(pos + 24, file.data.length, true);
      view.setUint16(pos + 28, file.name.length, true);
      view.setUint16(pos + 30, 0, true); // Extra
      view.setUint16(pos + 32, 0, true); // Comment
      view.setUint16(pos + 34, 0, true); // Disk
      view.setUint16(pos + 36, 0, true); // Internal attr
      view.setUint32(pos + 38, 0, true); // External attr
      view.setUint32(pos + 42, file.offset, true);

      pos += 46;
      const nameBytes = new TextEncoder().encode(file.name);
      new Uint8Array(buffer, pos, nameBytes.length).set(nameBytes);
      pos += nameBytes.length;
    }

    // 3. EOCD
    view.setUint32(pos, 0x06054b50, true);
    view.setUint16(pos + 4, 0, true);
    view.setUint16(pos + 6, 0, true);
    view.setUint16(pos + 8, this.files.length, true);
    view.setUint16(pos + 10, this.files.length, true);
    view.setUint32(pos + 12, centralDirectorySize, true);
    view.setUint32(pos + 16, centralDirectoryOffset, true);
    view.setUint16(pos + 20, 0, true);

    return buffer;
  }

  private calculateCrc32(data: Uint8Array): number {
    let crc = 0 ^ (-1);
    for (let i = 0; i < data.length; i++) {
        crc = (crc >>> 8) ^ this.crcTable[(crc ^ data[i]) & 0xFF];
    }
    return (crc ^ (-1)) >>> 0;
  }

  private crcTable = (() => {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
        let c = i;
        for (let j = 0; j < 8; j++) {
            c = ((c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1));
        }
        table[i] = c;
    }
    return table;
  })();
}
